import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Loader2,
  Gavel,
  AlertTriangle,
  Clock,
  Send,
  CheckCircle2,
  ClipboardList,
  Sparkles,
  ArrowRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";

const SECTIONS = [
  { key: "needs_decision", label: "Needs CEO Decision", icon: Gavel, tone: "gold" },
  { key: "ready_to_delegate", label: "Ready to Delegate", icon: Send, tone: "gold" },
  { key: "assistant_prepared", label: "Assistant Prepared", icon: ClipboardList, tone: "muted" },
  { key: "active_delegations", label: "Active Delegations", icon: Sparkles, tone: "muted" },
  { key: "waiting", label: "Waiting for Response", icon: Clock, tone: "muted" },
  { key: "overdue", label: "Overdue", icon: AlertTriangle, tone: "red" },
  { key: "follow_up_today", label: "Follow Up Today", icon: Clock, tone: "gold" },
  { key: "completed_today", label: "Completed Today", icon: CheckCircle2, tone: "green" },
  { key: "upcoming", label: "Upcoming", icon: Clock, tone: "muted" },
];

const TONE_STYLES = {
  gold: "border-champagne/30 text-champagne",
  muted: "border-seam text-textmuted",
  red: "border-[#5C2B29]/50 text-[#E5A09E]",
  green: "border-[#4D5B46]/50 text-[#A3C293]",
};

const CommandDesk = () => {
  const { user, admin, bootstrapAvailable, refreshAdmin } = useAuth();
  const navigate = useNavigate();
  const [desk, setDesk] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bootstrapping, setBootstrapping] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/enterprise/desk");
      setDesk(data);
    } catch (err) {
      if (err.response?.status !== 403) {
        toast.error(err.response?.data?.detail || "Could not load desk");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (admin) load();
    else setLoading(false);
  }, [admin, load]);

  const claimOwnership = async () => {
    setBootstrapping(true);
    try {
      await api.post("/admin/bootstrap");
      toast.success("Owner / CEO authority claimed.");
      await refreshAdmin();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bootstrap failed");
    } finally {
      setBootstrapping(false);
    }
  };

  if (!admin && bootstrapAvailable) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto regal-card p-10 mt-8 text-center tracing-beam">
          <p className="label-eyebrow">R.S.V.P. Enterprise OS</p>
          <h1 className="font-heading text-4xl mt-3 mb-3">Claim Owner / CEO authority</h1>
          <p className="text-textmuted leading-relaxed mb-6">
            No administrator exists yet. As the first authenticated user you may claim
            Owner / CEO Super Admin. This is a one-time bootstrap — future admins must be
            invited by an existing Owner / CEO.
          </p>
          <button
            type="button"
            onClick={claimOwnership}
            disabled={bootstrapping}
            data-testid="bootstrap-btn"
            className="inline-flex items-center gap-2 bg-champagne text-obsidian hover:bg-champagne-soft font-medium rounded-md px-6 py-3 transition-all shadow-[0_0_25px_rgba(212,175,55,0.2)] disabled:opacity-60"
          >
            {bootstrapping ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gavel className="w-4 h-4" strokeWidth={1.5} />}
            Claim Owner / CEO Super Admin
          </button>
          <p className="text-xs text-textmuted mt-4">Signed in as {user?.email}</p>
        </div>
      </Layout>
    );
  }

  if (!admin) {
    return (
      <Layout>
        <div className="max-w-xl mx-auto regal-card p-10 mt-8 text-center">
          <h1 className="font-heading text-3xl mb-3">Access limited</h1>
          <p className="text-textmuted">
            You do not currently have R.S.V.P. Enterprise admin access. Ask an Owner / CEO
            to invite you from the Admin Access Center.
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="mb-8">
        <p className="label-eyebrow">Executive Desk</p>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight mt-2">
          Command Desk
        </h1>
        <p className="text-textmuted mt-3 max-w-2xl leading-relaxed">
          At-a-glance authority. Approvals waiting, delegations ready, overdue matters,
          and today's follow-ups — all in one composed view.
        </p>
      </section>

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="w-6 h-6 text-champagne animate-spin" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-8">
            {SECTIONS.slice(0, 5).map((s) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.key}
                  data-testid={`desk-kpi-${s.key}`}
                  className={`regal-card p-4 border ${TONE_STYLES[s.tone]}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="label-eyebrow text-[10px]">{s.label}</span>
                    <Icon className="w-4 h-4 opacity-70" strokeWidth={1.4} />
                  </div>
                  <p className="font-sans text-3xl font-light mt-2 text-textmain">
                    {desk?.counts?.[s.key] ?? 0}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {SECTIONS.map((s) => {
              const items = desk?.sections?.[s.key] || [];
              const Icon = s.icon;
              return (
                <div
                  key={s.key}
                  data-testid={`desk-section-${s.key}`}
                  className="regal-card overflow-hidden"
                >
                  <div className="px-5 py-3 border-b border-seam flex items-center justify-between bg-elevated/40">
                    <div className="flex items-center gap-2">
                      <Icon className={`w-4 h-4 ${TONE_STYLES[s.tone].split(' ')[1]}`} strokeWidth={1.5} />
                      <h3 className="font-heading text-lg">{s.label}</h3>
                    </div>
                    <span className="text-xs text-textmuted">{items.length}</span>
                  </div>
                  <div className="max-h-72 overflow-y-auto divide-y divide-seam">
                    {items.length === 0 ? (
                      <div className="px-5 py-6 text-xs text-textmuted text-center opacity-60">Nothing here.</div>
                    ) : (
                      items.map((t) => (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => navigate(`/enterprise/tasks?id=${t.id}`)}
                          className="w-full text-left px-5 py-3 hover:bg-elevated/40 transition-colors flex items-start justify-between gap-3 group"
                        >
                          <div className="min-w-0">
                            <p className="text-sm text-textmain truncate">{t.title}</p>
                            <p className="text-[11px] text-textmuted mt-0.5">
                              {t.kind.replace(/_/g, " ")} · {t.priority}
                              {t.deadline ? ` · due ${t.deadline.slice(0, 10)}` : ""}
                            </p>
                          </div>
                          <ArrowRight className="w-3.5 h-3.5 text-textmuted group-hover:text-champagne mt-1" strokeWidth={1.5} />
                        </button>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </Layout>
  );
};

export default CommandDesk;
